class CustomSelect extends HTMLElement {
  constructor() {
    super();

    // State management
    this.isOpen = false;

    // DOM references
    this.trigger = this.querySelector(".options__trigger");
    this.drawer = this.querySelector(".options__drawer");
    this.closeButton = this.querySelector(".options__close");
    this.options = this.querySelectorAll(".options__item");
    this.currentValue = this.querySelector(".options__value");
    this.handleClickOutside = this.handleClickOutside.bind(this);

    // Create overlay for mobile
    this.overlay = document.createElement("div");
    this.overlay.className = "options__overlay";
    this.insertBefore(this.overlay, this.drawer);
  }

  // Sets up all event listeners
  connectedCallback() {
    document.addEventListener("click", this.handleClickOutside);

    // Toggle on trigger click
    this.trigger.addEventListener("click", () => this.toggle());

    // Close on overlay click
    this.overlay.addEventListener("click", () => this.close());

    // Close button if present
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }

    // Set up option selection
    this.options.forEach((option) => {
      option.addEventListener("click", () => this.selectOption(option));
      option.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.selectOption(option);
        }
      });
    });

    // Close on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Handle keyboard navigation
    this.drawer.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        this.handleArrowKeys(e);
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.handleClickOutside);
  }

  handleClickOutside(e) {
    if (this.isOpen && !this.contains(e.target)) {
      this.close();
    }
  }

  // Toggles the dropdown open/closed
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.setAttribute("open", "");
    this.trigger.setAttribute("aria-expanded", "true");
  }

  close() {
    this.isOpen = false;
    this.removeAttribute("open");
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.focus();
  }

  /**
   * Handles option selection
   * @param {HTMLElement} option - The selected option element
   */
  selectOption(option) {
    const value = option.getAttribute("value");
    const text = option.textContent;

    // Update selected value display
    this.currentValue.textContent = text;

    // Update aria-selected states
    this.options.forEach((opt) => {
      opt.setAttribute("aria-selected", opt === option ? "true" : "false");
    });

    // Close the drawer
    this.close();

    /**
     * Dispatches change event
     * @param {string} value - The value of selected option
     * @param {string} text - The text of selected option
     */
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value, text },
      })
    );

    // You could also submit a form or navigate here if needed
    // window.location.href = window.location.pathname + '?sort_by=' + value;
  }

  handleArrowKeys(e) {
    const current = document.activeElement;
    const options = Array.from(this.options);
    const currentIndex = options.indexOf(current);

    if (e.key === "ArrowDown") {
      const nextIndex = (currentIndex + 1) % options.length;
      options[nextIndex].focus();
    } else if (e.key === "ArrowUp") {
      const prevIndex = (currentIndex - 1 + options.length) % options.length;
      options[prevIndex].focus();
    } else if (e.key === "Escape") {
      this.close();
    }
  }
}

customElements.define("custom-select", CustomSelect);
